import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, GraduationCap, Loader2, ShieldAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const BAND_STYLE = { low: "bg-emerald-50 text-emerald-700", medium: "bg-amber-50 text-amber-800", high: "bg-red-50 text-red-700" };

export default function SuccessionPanel() {
  const [data, setData] = useState(null);
  const [queueing, setQueueing] = useState(null); // `${successor_id}|${skill}`

  useEffect(() => {
    api.get("/org/succession").then((r) => setData(r.data)).catch(() => setData({ panels: [] }));
  }, []);

  const queue = async (successor, skill, course) => {
    const key = `${successor.employee_id}|${skill}`;
    setQueueing(key);
    try {
      await api.post("/org/succession/queue", { successor_id: successor.employee_id, skill });
      toast.success(`Queued '${course?.title || skill}' for ${successor.name}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setQueueing(null);
    }
  };

  if (!data) return <Skeleton className="h-64 w-full rounded-none" />;

  return (
    <div className="space-y-4" data-testid="succession-panel">
      <p className="text-[13px] text-muted-foreground max-w-3xl">
        Single points of failure with ranked internal backups. Queue the closing Coursera track with one
        click — the successor and their manager are notified automatically.
      </p>
      {data.panels.map((p) => (
        <div key={p.finding_id} className="bg-white border border-border" data-testid={`succession-card-${p.employee.id}`}>
          <div className="px-5 py-4 border-b border-border flex items-center gap-3 flex-wrap">
            <ShieldAlert className="h-4 w-4 text-red-600" />
            <div>
              <p className="text-[14px] font-bold text-[var(--brand-primary)]">{p.employee.name}</p>
              <p className="text-[11px] text-muted-foreground">{p.employee.title} · {p.employee.department} · <span className="font-mono">{p.employee.id}</span></p>
            </div>
            <span className={`ml-auto text-[10px] font-mono px-2 py-1 ${BAND_STYLE[p.attrition.band]}`} data-testid={`succession-attrition-${p.employee.id}`}>
              attrition {p.attrition.score} · {p.attrition.band.toUpperCase()}
            </span>
          </div>
          <div className="divide-y divide-border">
            {p.skills.map((sp) => (
              <div key={sp.skill} className="px-5 py-4 grid grid-cols-1 lg:grid-cols-[220px_1fr_320px] gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Critical skill</p>
                  <p className="text-[13px] font-semibold text-slate-800 mt-1">{sp.skill}</p>
                  {sp.closing_course && (
                    <a href={sp.closing_course.url} target="_blank" rel="noreferrer"
                      className="mt-2 inline-flex items-start gap-1.5 text-[11px] text-teal-700 hover:text-teal-900">
                      <GraduationCap className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>{sp.closing_course.title} <span className="text-muted-foreground">({sp.closing_course.partner})</span>
                        <ExternalLink className="h-2.5 w-2.5 inline ml-1" /></span>
                    </a>
                  )}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold mb-1.5">Best-ready internal backups</p>
                  <div className="border border-border divide-y divide-border">
                    {sp.successors.map((s) => (
                      <div key={s.employee_id} className="flex items-center justify-between px-3 py-2">
                        <span className="text-[12px] text-slate-800">
                          {s.name} <span className="text-muted-foreground text-[11px] ml-1">{s.title}</span>
                        </span>
                        <span className="flex items-center gap-3">
                          <span className="text-[11px] font-mono text-slate-500">
                            {s.cross_train ? "cross-train" : `${s.readiness_pct}% ready`}
                          </span>
                          <button
                            data-testid={`queue-course-${s.employee_id}-${sp.skill.replace(/[^a-z0-9]+/gi, "-")}`}
                            disabled={queueing === `${s.employee_id}|${sp.skill}`}
                            onClick={() => queue(s, sp.skill, sp.closing_course)}
                            className="text-[11px] text-white bg-teal-700 hover:bg-teal-800 px-2.5 py-1 disabled:opacity-50 inline-flex items-center gap-1"
                          >
                            {queueing === `${s.employee_id}|${sp.skill}` ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                            Queue course
                          </button>
                        </span>
                      </div>
                    ))}
                    {!sp.successors.length && (
                      <p className="px-3 py-2 text-[12px] text-muted-foreground">No internal candidates — external hire recommended.</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {!data.panels.length && (
        <p className="text-[13px] text-muted-foreground border border-border bg-white p-6">No single points of failure detected.</p>
      )}
    </div>
  );
}
